import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Search } from 'lucide-react';
import { API_BASE_URL } from '../config/config';

// 옛 "관리자 그룹 등록/삭제"(AdminGroupRegister.js) 의 구성원 기능을 옮긴 것이다. 규격: spec D1.
// API 네 개와 요청 모양(경로·파라미터·본문·헤더)은 옛 화면 그대로다 — axios 를 직접
// 부른다(useApi 아님). 옮기며 바뀐 것은 상태를 부모(AdminGroupRegister) 대신 이 컴포넌트가
// 들고, selectedGroup.id 대신 groupId prop 을 쓰는 것뿐이다.
const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return `${date.getFullYear()}. ${date.getMonth() + 1}. ${date.getDate()}.`;
};

export default function AdminGroupMembers({ groupId }) {
    const [groupUsers, setGroupUsers] = useState([]);
    const [isLoadingMembers, setIsLoadingMembers] = useState(true);
    const [error, setError] = useState(null);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [userSearchError, setUserSearchError] = useState(null);
    const [isUserSearching, setIsUserSearching] = useState(false);
    const [isAddingUser, setIsAddingUser] = useState(null);
    const [isRemovingUser, setIsRemovingUser] = useState(null);

    // AdminGroupRegister.js:279-311 (handleGroupClick 의 목록 조회 부분) 그대로.
    const loadMembers = useCallback(async () => {
        setError(null);
        setIsLoadingMembers(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE_URL}/api/group-users/${groupId}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    timeout: 15000
                }
            );

            if (response.data.success) {
                if (response.data.message === '등록된 사용자가 없습니다.') {
                    setGroupUsers([]);
                } else {
                    setGroupUsers(response.data.users || []);
                }
            } else {
                setError(response.data.message || '사용자 목록을 불러오는데 실패했습니다.');
            }
        } catch (err) {
            console.error('사용자 정보 조회 실패:', err);
            if (err.response?.status === 504) {
                setError('서버 응답 시간이 초과되었습니다. 다시 시도해 주세요.');
            } else if (err.response?.status === 404) {
                setError('해당 그룹을 찾을 수 없습니다.');
            } else {
                setError(err.response?.data?.message || '사용자 정보를 불러오는 중 오류가 발생했습니다.');
            }
        } finally {
            setIsLoadingMembers(false);
        }
    }, [groupId]);

    useEffect(() => { loadMembers(); }, [loadMembers]);

    // AdminGroupRegister.js:332-362 그대로.
    const handleUserSearch = async () => {
        if (!userSearchTerm.trim()) {
            alert('검색어를 입력해주세요.');
            return;
        }

        setIsUserSearching(true);
        setUserSearchError(null);

        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/api/admin-users/search`, {
                params: {
                    term: userSearchTerm,
                    group_id: groupId
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setUserSearchResults(response.data.users || []);
                if (response.data.users.length === 0) {
                    setUserSearchError('검색 결과가 없습니다.');
                }
            } else {
                setUserSearchError(response.data.message || '사용자 검색에 실패했습니다.');
                setUserSearchResults([]);
            }
        } catch (err) {
            console.error('사용자 검색 실패:', err);
            setUserSearchError(err.response?.data?.message || '사용자 검색 중 오류가 발생했습니다.');
            setUserSearchResults([]);
        } finally {
            setIsUserSearching(false);
        }
    };

    // AdminGroupRegister.js:378-402 그대로(끝에 handleGroupClick(selectedGroup) 대신 loadMembers()).
    const handleAddUserToGroup = async (user) => {
        setIsAddingUser(user.user_id);

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API_BASE_URL}/api/group-users`,
                {
                    group_id: groupId,
                    user_id: user.user_id
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                await loadMembers();
                setUserSearchResults([]);
                setUserSearchTerm('');
                alert('사용자가 그룹에 등록되었습니다.');
            } else {
                throw new Error(response.data.message);
            }
        } catch (err) {
            console.error('사용자 등록 실패:', err);
            alert(err.response?.data?.message || '사용자 등록 중 오류가 발생했습니다.');
        } finally {
            setIsAddingUser(null);
        }
    };

    // AdminGroupRegister.js:420-451 그대로(끝의 목록 새로고침은 같은 요청을 부르는
    // loadMembers() 로 합쳤다 — 요청 자체는 바뀌지 않았다).
    const handleRemoveUserFromGroup = async (user) => {
        if (!window.confirm('정말로 이 사용자를 그룹에서 제거하시겠습니까?')) {
            return;
        }

        setIsRemovingUser(user.id);

        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${API_BASE_URL}/api/group-users/${groupId}/${user.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            await loadMembers();
            alert('사용자가 그룹에서 제거되었습니다.');
        } catch (err) {
            console.error('사용자 삭제 실패:', err);
            alert(err.response?.data?.message || '사용자 삭제 중 오류가 발생했습니다.');
        } finally {
            setIsRemovingUser(null);
        }
    };

    return (
        <div className="group-members">
            {error && <div className="sds-notice sds-notice--error">{error}</div>}

            <div className="sds-toolbar">
                <input
                    className="sds-input group-members__search"
                    placeholder="사용자 이름 또는 이메일로 검색..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleUserSearch(); }}
                />
                <button className="sds-btn sds-btn--secondary" onClick={handleUserSearch} disabled={isUserSearching}>
                    <Search size={16} aria-hidden="true" />{isUserSearching ? '검색 중...' : '검색'}
                </button>
            </div>

            {userSearchError && <div className="sds-notice sds-notice--error">{userSearchError}</div>}

            {userSearchResults.length > 0 && (
                <>
                    <h3 className="group-members__title">검색 결과</h3>
                    <div className="sds-table-wrap">
                        <table className="sds-table">
                            <thead><tr><th>이름</th><th>이메일</th><th>연락처</th><th aria-label="작업" /></tr></thead>
                            <tbody>
                                {userSearchResults.map((u) => (
                                    <tr key={u.user_id}>
                                        <td><b>{u.name}</b></td><td>{u.email}</td><td>{u.mobile}</td>
                                        <td>
                                            <button className="sds-btn sds-btn--weak sds-btn--sm" onClick={() => handleAddUserToGroup(u)} disabled={isAddingUser === u.user_id}>
                                                {isAddingUser === u.user_id ? '등록 중...' : '등록'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <h3 className="group-members__title">등록된 관리자 <b>{groupUsers.length}</b>명</h3>
            {isLoadingMembers ? (
                <div className="sds-notice">데이터를 불러오는 중입니다...</div>
            ) : groupUsers.length === 0 ? (
                <div className="sds-empty">
                    <p>등록된 사용자가 없습니다.</p>
                    <p>사용자 검색 후 등록해 주세요.</p>
                </div>
            ) : (
                <div className="sds-table-wrap">
                    <table className="sds-table">
                        <thead><tr><th>이름</th><th>연락처</th><th>등록일</th><th aria-label="작업" /></tr></thead>
                        <tbody>
                            {groupUsers.map((u) => (
                                <tr key={u.id}>
                                    <td><b>{u.name}</b></td><td>{u.mobile}</td>
                                    <td className="sds-table__muted">{formatDate(u.member_created_at || u.created_at)}</td>
                                    <td>
                                        <button className="sds-btn sds-btn--ghost sds-btn--sm" onClick={() => handleRemoveUserFromGroup(u)} disabled={isRemovingUser === u.id}>
                                            {isRemovingUser === u.id ? '삭제 중...' : '삭제'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
